import React, { useState, useCallback } from 'react';
import { API_BASE, authFetch } from '../services/api';
import githubIcon from '../github (1).png';
import bitbucketIcon from '../bitbucket (1).png';

const PROVIDERS = [
  { id: 'github', label: 'GitHub', icon: githubIcon, color: '#24292e' },
  { id: 'bitbucket', label: 'Bitbucket', icon: bitbucketIcon, color: '#0052cc' },
];

export default function RepoPickerModal({ onSelect, onCancel }) {
  const [step, setStep] = useState('pick'); // pick | loading | repos | importing
  const [provider, setProvider] = useState(null);
  const [token, setToken] = useState(null);
  const [repos, setRepos] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(null);

  const readStoredOAuthResult = () => {
    try {
      const raw = localStorage.getItem('oauth_result');
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (data?.type === 'oauth_callback') {
        localStorage.removeItem('oauth_result');
        return data;
      }
    } catch (_) {}
    return null;
  };

  const pollOAuthToken = async (prov, oauthState) => {
    const maxAttempts = 40;
    for (let i = 0; i < maxAttempts; i++) {
      const pollRes = await authFetch(`/api/oauth/${prov}/poll?state=${encodeURIComponent(oauthState)}`);
      if (!pollRes.ok) {
        const e = await pollRes.json().catch(() => ({}));
        throw new Error(e.detail || 'Failed to retrieve OAuth token');
      }
      const pollData = await pollRes.json();
      if (pollData.ready && pollData.token) return pollData.token;
      await new Promise(r => setTimeout(r, 500));
    }
    throw new Error('Timed out waiting for authentication. Please try again.');
  };

  const startOAuth = useCallback(async (prov) => {
    setError('');
    setProvider(prov);
    setStep('loading');

    try {
      const res = await authFetch(`/api/oauth/${prov}/authorize`);
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed to get auth URL'); }
      const { url } = await res.json();

      const popup = window.open(url, `oauth_${prov}_${Date.now()}`, 'width=600,height=700,scrollbars=yes');
      if (!popup) { throw new Error('Popup blocked. Please allow popups for this site.'); }

      let completed = false;
      let checkClosed = null;

      const fail = (message) => {
        if (completed) return;
        completed = true;
        cleanup();
        setError(message);
        setStep('pick');
      };

      const succeed = async (data) => {
        if (completed) return;
        if (data.status === 'error') {
          fail(data.payload || 'OAuth failed');
          return;
        }
        const oauthState = data.payload;
        if (!oauthState) {
          fail('Invalid OAuth response');
          return;
        }
        try {
          const tok = await pollOAuthToken(prov, oauthState);
          if (completed) return;
          completed = true;
          cleanup();
          setToken(tok);
          await fetchRepos(prov, tok, 1);
        } catch (e) {
          fail(e.message);
        }
      };

      const onMessage = (event) => {
        if (event.data?.type !== 'oauth_callback') return;
        if (event.data.provider && event.data.provider !== prov) return;
        succeed(event.data);
      };

      const onStorage = (event) => {
        if (event.key !== 'oauth_result') return;
        try {
          const data = JSON.parse(event.newValue);
          if (data?.type === 'oauth_callback') {
            if (data.provider && data.provider !== prov) return;
            localStorage.removeItem('oauth_result');
            succeed(data);
          }
        } catch (_) {}
      };

      const cleanup = () => {
        window.removeEventListener('message', onMessage);
        window.removeEventListener('storage', onStorage);
        if (checkClosed) clearInterval(checkClosed);
      };

      window.addEventListener('message', onMessage);
      window.addEventListener('storage', onStorage);

      // Bitbucket often clears window.opener; wait for localStorage before treating close as cancel
      checkClosed = setInterval(() => {
        if (!popup.closed) return;
        clearInterval(checkClosed);
        setTimeout(() => {
          if (completed) return;
          const stored = readStoredOAuthResult();
          if (stored) {
            succeed(stored);
            return;
          }
          fail('Authentication cancelled.');
        }, 700);
      }, 400);

    } catch (e) {
      setError(e.message);
      setStep('pick');
    }
  }, []);

  const fetchRepos = async (prov, tok, page = 1) => {
    setStep('loading');
    try {
      const res = await authFetch(`/api/oauth/${prov}/repos?token=${encodeURIComponent(tok)}&page=${page}`);
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        const detail = e.detail;
        throw new Error(typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : 'Failed to fetch repos'));
      }
      const data = await res.json();
      setRepos(data.repos);
      setStep('repos');
    } catch (e) {
      setError(e.message);
      setStep('pick');
    }
  };

  const handleSelectRepo = async (repo) => {
    setImporting(repo.id);
    setStep('importing');
    try {
      const payload = {
        git_url: repo.url,
        project_name: repo.name.split('/').pop(),
      };
      // Backend embeds credentials; never put tokens in the URL from the browser
      if (token && (provider === 'bitbucket' || repo.private)) {
        payload.git_token = token;
      }

      const res = await authFetch('/api/projects/import-git', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Import failed'); }
      const data = await res.json();
      onSelect(data);
    } catch (e) {
      setError(e.message);
      setStep('repos');
      setImporting(null);
    }
  };

  const filtered = repos.filter(r =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={s.overlay} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={s.modal}>
        {/* Header */}
        <div style={s.header}>
          <h2 style={s.title}>
            {step === 'pick' && 'Connect Repository'}
            {step === 'loading' && 'Connecting...'}
            {step === 'repos' && ` ${provider === 'github' ? 'GitHub' : 'Bitbucket'} Repositories`}
            {step === 'importing' && 'Importing...'}
          </h2>
          <button onClick={onCancel} style={s.closeBtn}>✕</button>
        </div>

        {/* Error */}
        {error && <div style={s.error}>⚠ {error}</div>}

        {/* Step: Pick provider */}
        {step === 'pick' && (
          <div style={s.providerList}>
            <p style={s.hint}>Choose a platform to browse and import your repositories, including private ones.</p>
            {PROVIDERS.map(p => (
              <button key={p.id} onClick={() => startOAuth(p.id)} style={{ ...s.providerBtn, borderColor: p.color }}>
                <img src={p.icon} alt={p.label} style={{ ...s.providerIcon, ...(p.id === 'github' ? { filter: 'var(--icon-filter)' } : {}) }} />
                <div>
                  <div style={s.providerName}>Continue with {p.label}</div>
                  <div style={s.providerSub}>Access public & private repositories</div>
                </div>
                <span style={s.arrow}>→</span>
              </button>
            ))}
          </div>
        )}

        {/* Step: Loading */}
        {step === 'loading' && (
          <div style={s.center}>
            <div style={s.spinner} />
            <p style={s.loadingText}>Waiting for authentication...</p>
            <p style={s.loadingHint}>Complete the login in the popup window</p>
          </div>
        )}

        {/* Step: Repo list */}
        {step === 'repos' && (
          <>
            <div style={s.searchRow}>
              <input
                style={s.search}
                placeholder="Search repositories..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
              <button onClick={async () => {
                if (token && provider) {
                  await authFetch(`/api/oauth/${provider}/revoke?token=${encodeURIComponent(token)}`, { method: 'POST' }).catch(() => {});
                }
                setStep('pick'); setRepos([]); setToken(null); setSearch('');
              }} style={s.switchBtn}>
                🔌 Disconnect
              </button>
            </div>
            <div style={s.repoList}>
              {filtered.length === 0 && <p style={s.empty}>No repositories found.</p>}
              {filtered.map(repo => (
                <button key={repo.id} onClick={() => handleSelectRepo(repo)} style={s.repoItem}>
                  <div style={s.repoLeft}>
                    <span style={s.repoName}>{repo.name}</span>
                    {repo.private && <span style={s.privateBadge}>Private</span>}
                    {repo.language && <span style={s.langBadge}>{repo.language}</span>}
                    {repo.description && <p style={s.repoDesc}>{repo.description}</p>}
                  </div>
                  <span style={s.importBtn}>Import →</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Step: Importing */}
        {step === 'importing' && (
          <div style={s.center}>
            <div style={s.spinner} />
            <p style={s.loadingText}>Cloning repository...</p>
            <p style={s.loadingHint}>This may take a moment for large repos</p>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'var(--bg-secondary, #1e1e2e)', border: '1px solid var(--border, #2d2d3d)', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 16px', borderBottom: '1px solid var(--border, #2d2d3d)' },
  title: { fontSize: 17, fontWeight: 700, color: 'var(--text-primary, #e2e8f0)', margin: 0 },
  closeBtn: { background: 'transparent', border: 'none', color: 'var(--text-muted, #64748b)', fontSize: 18, cursor: 'pointer', padding: '2px 6px' },
  error: { margin: '0 24px 0', padding: '10px 14px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 8, fontSize: 13, color: '#f87171' },
  providerList: { padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 },
  hint: { fontSize: 13, color: 'var(--text-muted, #64748b)', margin: '0 0 8px' },
  providerBtn: { display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', background: 'rgba(255,255,255,0.03)', border: '1px solid', borderRadius: 10, cursor: 'pointer', textAlign: 'left', transition: 'background 0.15s' },
  providerIcon: { width: 28, height: 28, objectFit: 'contain' },
  providerName: { fontSize: 15, fontWeight: 600, color: 'var(--text-primary, #e2e8f0)' },
  providerSub: { fontSize: 12, color: 'var(--text-muted, #64748b)', marginTop: 2 },
  arrow: { marginLeft: 'auto', fontSize: 18, color: 'var(--text-muted, #64748b)' },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', gap: 12 },
  spinner: { width: 36, height: 36, border: '3px solid rgba(99,102,241,0.2)', borderTop: '3px solid #6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingText: { fontSize: 15, fontWeight: 600, color: 'var(--text-primary, #e2e8f0)', margin: 0 },
  loadingHint: { fontSize: 13, color: 'var(--text-muted, #64748b)', margin: 0 },
  searchRow: { display: 'flex', gap: 10, padding: '16px 24px 12px', borderBottom: '1px solid var(--border, #2d2d3d)' },
  search: { flex: 1, padding: '9px 14px', background: 'var(--bg-card, #16213e)', border: '1px solid var(--border, #2d2d3d)', borderRadius: 8, color: 'var(--text-primary, #e2e8f0)', fontSize: 13, outline: 'none' },
  switchBtn: { padding: '9px 14px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 8, color: '#f87171', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 },
  repoList: { overflowY: 'auto', flex: 1, padding: '8px 12px 16px' },
  empty: { textAlign: 'center', color: 'var(--text-muted, #64748b)', fontSize: 13, padding: '32px 0' },
  repoItem: { display: 'flex', alignItems: 'center', width: '100%', padding: '12px 14px', background: 'transparent', border: '1px solid transparent', borderRadius: 8, cursor: 'pointer', textAlign: 'left', marginBottom: 4, transition: 'background 0.1s' },
  repoLeft: { flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 },
  repoName: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary, #e2e8f0)' },
  privateBadge: { fontSize: 10, fontWeight: 600, padding: '2px 7px', background: 'rgba(251,191,36,0.15)', color: '#fbbf24', borderRadius: 4, border: '1px solid rgba(251,191,36,0.3)' },
  langBadge: { fontSize: 10, padding: '2px 7px', background: 'rgba(99,102,241,0.15)', color: '#a5b4fc', borderRadius: 4 },
  repoDesc: { width: '100%', fontSize: 12, color: 'var(--text-muted, #64748b)', margin: 0 },
  importBtn: { fontSize: 13, color: '#6366f1', fontWeight: 600, whiteSpace: 'nowrap', marginLeft: 8 },
};
